import type { AnalyticsMetricName, AnalyticsSplitName } from "@lafayette/schemas";
import type { MouseEvent } from "react";
import { Link, useNavigate } from "react-router";

import { Icon } from "../../ui/icon";
import { exploreHref } from "../explore/explore-address";

// "Click any number or chart to zoom in" (ADR 0017 and the Paper hover state of "Activity over time"): a card or a tile on the Overview opens
// Explore with its metric. The "Zoom in" button is a real link, so a keyboard user and a screen reader reach it; it shows on hover and on focus.
// A click on the card itself does the same, except on a control inside the card (a link, a button, the details of a chart).

const INTERACTIVE = "a, button, summary, details, input, select, textarea, label";

/** The click handler of a card: it zooms unless the click was on a control inside the card. */
export function useZoomOnClick(href: string) {
  const navigate = useNavigate();
  return (event: MouseEvent<HTMLElement>) => {
    if (event.defaultPrevented || (event.target as Element).closest(INTERACTIVE) !== null) return;
    navigate(href);
  };
}

export function zoomHref(
  metric: AnalyticsMetricName,
  range: string,
  split: AnalyticsSplitName = "none",
): string {
  return exploreHref(metric, range, split);
}

export function ZoomInButton({ href, label }: Readonly<{ href: string; label: string }>) {
  return (
    <Link
      to={href}
      className="inline-flex h-[30px] shrink-0 items-center gap-1.5 rounded-[9px] bg-primary px-2.5 text-xs font-semibold text-primary-foreground no-underline opacity-0 transition-opacity group-focus-within:opacity-100 group-hover:opacity-100 focus-visible:opacity-100 compact:h-7"
    >
      <Icon name="zoom-in-area" size="sm" />
      Zoom in
      <span className="sr-only">: {label}</span>
    </Link>
  );
}
